/**
 * Shopify product fields (BLU-1376, D11; handover 4.4). PrintRun reads a few
 * Shopify product metafields; this step adds them to the store in one click
 * through the shared `CreateDefinitionsBlock` (Settings, Product data lookup),
 * wrapped, never restyled: the wizard only passes opt-in props.
 *
 * Body: a "Shopify product metafields" list (Artwork files, Custom print SKU,
 * Fulfilment type, Pretreat), each "Added" or "Not added yet" from the status
 * the page already holds, then the block's one button "Add the fields". A
 * setup-required org has no namespace box (`fixedNamespace` `printrun`); an org
 * that already has mappings keeps its own namespace (the block seeds it as
 * today). A live org keeps the namespace box.
 *
 * Preview row (BLU-1399): when the run will go under `printrun` it also
 * creates Front preview and Back preview (variant mockup images for packing),
 * so the list gains a fifth row, Added once both preview purposes are mapped,
 * plus a note on where to upload them. The step decides that with the block's
 * own namespace rule (`firstMappingNamespace`, else `printrun` for a
 * setup-required org, else the block's default), reading the block's own
 * `list` query key, so one request serves both. The previews are not in the
 * status's required set, so they never block finishing setup.
 *
 * A design-ID (composition) org tracks a different preset; the four-row list
 * would mislabel it, so it keeps the plain "N of M mapped" line instead.
 *
 * Copy says "fields", never "designs": the preset excludes design_id
 * (single-writer invariant).
 *
 * Reads: the step's own `discoverMetafields` (a live Shopify call) runs on
 * mount, then at most once a minute and never on window focus; the block's
 * own invalidate after a run still refreshes it. A locked pane mounts no body.
 */
import { useTranslation } from 'react-i18next';
import { Check, Circle } from 'lucide-react';
import { ARTWORK_ORG_ONBOARDING_PURPOSES, PRINTRUN_METAFIELD_NAMESPACE } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { cn } from '@/lib/utils';
import { CreateDefinitionsBlock } from '@/features/organization/components/product-data-lookup/CreateDefinitionsBlock';
import type { DiscoveryStatus } from '@/features/organization/components/product-data-lookup/MetafieldPicker';
import {
  DEFAULT_DEFINITIONS_NAMESPACE,
  firstMappingNamespace,
} from '@/features/organization/components/product-data-lookup/definitions-namespace';
import { isSettledState } from '../../lib/step-state';
import { StepPane } from '../shell/StepPane';
import { useStepChrome } from '../shell/StepChrome';
import { paneActionClass } from '../shell/pane-action';

type MetafieldsStatus = { mappedRequired: number; requiredTotal: number; missingPurposes: string[] };

interface StepMetafieldsProps {
  metafields: MetafieldsStatus;
  /** The org is still in setup: no namespace box, fields go under `printrun`. */
  setupRequired: boolean;
  /** Refetch the onboarding status after the block filled mappings. */
  onProvisioned: () => void;
  /** After "Add the fields" succeeds (the page arms its auto-advance). */
  onSaved?: () => void;
}

type Row = { key: string; purposes: readonly string[] };

/** The four rows from the status, each with the preset purposes it stands for. */
const ROWS: readonly Row[] = [
  { key: 'stepMeta.row.artwork', purposes: ['front_artwork', 'back_artwork'] },
  { key: 'stepMeta.row.customPrintSku', purposes: ['custom_print_sku'] },
  { key: 'stepMeta.row.fulfilmentType', purposes: ['fulfillment_type'] },
  { key: 'stepMeta.row.pretreat', purposes: ['pretreat'] },
];

/** The optional fifth row: Added when BOTH preview purposes are in the mappings list. */
const PREVIEW_ROW: Row = {
  key: 'stepMeta.row.previews',
  purposes: ['front_mockup_preview', 'back_mockup_preview'],
};

/** The status tracks the artwork preset (every org that is not design-ID). */
function tracksArtworkPreset(metafields: MetafieldsStatus): boolean {
  const artwork = new Set<string>(ARTWORK_ORG_ONBOARDING_PURPOSES);
  return (
    metafields.requiredTotal === ARTWORK_ORG_ONBOARDING_PURPOSES.length &&
    metafields.missingPurposes.every((purpose) => artwork.has(purpose))
  );
}

export function StepMetafields({ metafields, setupRequired, onProvisioned, onSaved }: StepMetafieldsProps) {
  const { t } = useTranslation('onboarding');
  const { state } = useStepChrome();
  const artwork = tracksArtworkPreset(metafields);

  return (
    <StepPane
      title={t('stepMeta.title')}
      description={t('stepMeta.description')}
      summary={
        artwork
          ? t('stepMeta.summaryAdded')
          : t('stepMeta.summary', { mapped: metafields.mappedRequired, total: metafields.requiredTotal })
      }
    >
      {state === 'locked' ? null : (
        <StepMetafieldsBody
          metafields={metafields}
          artwork={artwork}
          setupRequired={setupRequired}
          onProvisioned={onProvisioned}
          onSaved={onSaved}
        />
      )}
    </StepPane>
  );
}

function StepMetafieldsBody({
  metafields,
  artwork,
  setupRequired,
  onProvisioned,
  onSaved,
}: Omit<StepMetafieldsProps, 'metafields'> & { metafields: MetafieldsStatus; artwork: boolean }) {
  const { t } = useTranslation('onboarding');
  const { isAdmin } = useRole();
  const { state, next } = useStepChrome();

  // Same derivation as ProductDataLookupCard: the block needs the discovery
  // state to know whether the store is reachable before it arms the button.
  const discoveryQuery = trpc.shopifyMetafieldMappings.discoverMetafields.useQuery(undefined, {
    retry: 0,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const discoveryStatus: DiscoveryStatus = discoveryQuery.isLoading
    ? 'loading'
    : discoveryQuery.isError
      ? 'errored'
      : (discoveryQuery.data?.length ?? 0) === 0
        ? 'empty'
        : 'loaded';

  // The block's own query key, so one request serves the block and this step.
  const listQuery = trpc.shopifyMetafieldMappings.list.useQuery(undefined, { staleTime: 60_000 });
  const runNamespace =
    firstMappingNamespace(listQuery.data) ??
    (setupRequired ? PRINTRUN_METAFIELD_NAMESPACE : DEFAULT_DEFINITIONS_NAMESPACE);
  const showPreviews = artwork && !listQuery.isLoading && runNamespace === PRINTRUN_METAFIELD_NAMESPACE;
  const mappedPurposes = new Set<string>((listQuery.data ?? []).map((m) => m.purpose));

  const missing = new Set(metafields.missingPurposes);
  const rows = showPreviews ? [...ROWS, PREVIEW_ROW] : ROWS;
  const isAdded = (row: Row) =>
    row === PREVIEW_ROW
      ? row.purposes.every((purpose) => mappedPurposes.has(purpose))
      : row.purposes.every((purpose) => !missing.has(purpose));
  // One visually primary button per pane: beside "Next" on a complete step
  // the block's button steps down to secondary (StepFooter does the same).
  const buttonTone = next && isSettledState(state) ? 'secondary' : 'primary';

  return (
    <div className="space-y-4">
      {artwork ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">{t('stepMeta.listLabel')}</p>
          <ul className="divide-y divide-border rounded-lg border border-line-strong">
            {rows.map((row) => {
              const added = isAdded(row);
              return (
                <li key={row.key} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3.5 py-2.5">
                  <span className="min-w-0 text-sm text-foreground">{t(row.key)}</span>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 text-xs font-medium',
                      added ? 'text-emerald-700' : 'text-muted-foreground',
                    )}
                  >
                    {added ? (
                      <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
                    ) : (
                      <Circle className="h-3 w-3" aria-hidden="true" />
                    )}
                    {added ? t('stepMeta.rowAdded') : t('stepMeta.rowMissing')}
                  </span>
                </li>
              );
            })}
          </ul>
          {showPreviews ? <p className="max-w-prose text-xs text-muted-foreground">{t('stepMeta.previewNote')}</p> : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground tabular-nums">
          {t('stepMeta.progress', { mapped: metafields.mappedRequired, total: metafields.requiredTotal })}
        </p>
      )}

      <p className="max-w-prose text-xs text-muted-foreground">{t('stepMeta.whyNote')}</p>

      <CreateDefinitionsBlock
        admin={isAdmin()}
        discoveryStatus={discoveryStatus}
        onAdvancedMappingsFilled={onProvisioned}
        onCreated={onSaved}
        onRetryDiscovery={() => void discoveryQuery.refetch()}
        buttonLabel={t('stepMeta.add')}
        fixedNamespace={setupRequired ? PRINTRUN_METAFIELD_NAMESPACE : undefined}
        embedded
        // The block's icon carries its own margin; the pane button already spaces it.
        buttonClassName={cn(paneActionClass(buttonTone), '[&_svg]:mr-0')}
      />
    </div>
  );
}
